import { Button, buttonVariants } from '@/components/ui/button';
import * as Icons from 'lucide-react';
import { User, Menu, ChevronDown, LogIn } from 'lucide-react';
import { getAppLink, getAuthLink } from '@/lib/links';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FLOW_EASE } from '@/components/marketing/motion';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState, useEffect } from 'react';
import {
  loadNavigationSettings,
  loadGeneralSettings,
  getFullAddress,
  getDefaultNavigationSettings,
  type MenuItem,
} from '@/lib/siteSettings';
import { ScriptInjector } from '@/components/scripts/ScriptInjector';
import { SEOHead } from '@/components/seo/SEOHead';

// Import local logo - will need to copy this asset
// import rooflyzerLogo from '@/assets/marketing/Rooflyzer Logo 1.png';

const footerLinkGroups = {
  Product: [
    { label: 'Features', to: '/features' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Estimator', to: '/estimator' },
    { label: 'Compare', to: '/compare' },
  ],
  Solutions: [
    { label: 'For Roofers', to: '/for-roofers' },
    { label: 'For Contractors', to: '/contractors' },
    { label: 'For Agencies', to: '/agencies' },
    { label: 'Lead Nurturing', to: '/nurture' },
  ],
  Resources: [
    { label: 'Blog', to: '/blog' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Changelog', to: '/changelog' },
    { label: 'Integrations', to: '/integrations' },
  ],
  Company: [
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
    { label: 'Privacy', to: '/privacy' },
    { label: 'Data Freedom', to: '/data-freedom' },
    { label: 'Terms', to: '/terms' },
  ],
};

const fallbackSeoByPath: Array<{ path: string; title: string; description: string }> = [
  {
    path: '/features',
    title: 'Roofing CRM Features',
    description:
      'Explore Rooflyzer OS features for roofing contractors: CRM, estimating, project management, automations, and reporting.',
  },
  {
    path: '/pricing',
    title: 'Roofing CRM Pricing',
    description:
      'See transparent Rooflyzer OS pricing plans for roofing businesses and agencies, including feature and scale breakdowns.',
  },
  {
    path: '/demo',
    title: 'Book a Rooflyzer Demo',
    description:
      'Schedule a personalized demo to see how Rooflyzer OS helps roofing teams capture leads and close more jobs.',
  },
  {
    path: '/compare',
    title: 'Compare Rooflyzer vs Alternatives',
    description:
      'Compare Rooflyzer against other roofing CRM platforms and see differences in workflow automation and growth outcomes.',
  },
  {
    path: '/blog',
    title: 'Rooflyzer Blog',
    description:
      'Read roofing industry insights, software updates, and operational playbooks from the Rooflyzer team.',
  },
  {
    path: '/contact',
    title: 'Contact Rooflyzer',
    description:
      'Get in touch with Rooflyzer for product questions, onboarding support, and partnership opportunities.',
  },
  {
    path: '/',
    title: 'Roofing CRM & Operating System for Contractors',
    description:
      'Rooflyzer OS is the all-in-one roofing business platform for lead capture, estimating, CRM, project workflows, and growth analytics.',
  },
];

function isValidMenuConfig(items: MenuItem[]): boolean {
  const isValidUrl = (url: string) => typeof url === 'string' && url.trim().startsWith('/');

  return items.every((item) => {
    if (item.children && item.children.length > 0) {
      return item.children.every((child) => isValidUrl(child.url));
    }
    return isValidUrl(item.url);
  });
}

const MobileMenu = ({
  menuItems,
  isLight = false,
}: {
  menuItems: MenuItem[];
  isLight?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`xl:hidden min-h-11 min-w-11 ${
            isLight ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10'
          }`}
          aria-label="Open navigation"
        >
          <Menu className="size-6" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        data-theme={isLight ? 'marketing-light' : 'marketing-dark'}
        aria-describedby={undefined}
        className={`w-[300px] p-6 overflow-y-auto ${
          isLight
            ? 'bg-white border-gray-200 text-[#181B20]'
            : 'bg-[#0e061a] border-white/10 text-white'
        }`}
      >
        <SheetTitle className="sr-only">Main navigation</SheetTitle>
        <nav aria-label="Mobile navigation" className="flex flex-col gap-6 mt-10">
          <a
            href={getAppLink('/qr-hub-landing')}
            onClick={() => setOpen(false)}
            className={buttonVariants({
              variant: 'ghost',
              className: `w-full min-h-11 justify-start text-lg ${
                isLight ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`,
            })}
          >
            QR Print Shop
          </a>
          {menuItems
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((item) => {
              if (item.children && item.children.length > 0) {
                return (
                  <div key={item.url} className="space-y-2">
                    <div
                      className={`text-sm font-semibold uppercase tracking-wider px-2 ${
                        isLight ? 'text-gray-500' : 'text-white/80'
                      }`}
                    >
                      {item.label}
                    </div>
                    {item.children
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((child) => {
                        const IconComponent = child.icon
                          ? (
                              Icons as unknown as Record<
                                string,
                                React.ComponentType<{ className?: string }>
                              >
                            )[child.icon]
                          : null;
                        return (
                          <Link
                            key={child.url}
                            to={child.url}
                            onClick={() => setOpen(false)}
                            className={`flex items-start gap-3 p-2 rounded-lg transition-colors pl-4 ${
                              location.pathname === child.url
                                ? isLight
                                  ? 'text-[#7950A2] bg-[#F2ECF7] font-semibold'
                                  : 'text-white bg-white/10 font-medium'
                                : isLight
                                  ? 'text-gray-700 hover:bg-gray-50'
                                  : 'text-white/80 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            {IconComponent && (
                              <IconComponent className="size-4 mt-0.5 text-[#7950A2] flex-shrink-0" />
                            )}
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{child.label}</span>
                              {child.description && (
                                <span
                                  className={`text-[10px] line-clamp-1 mt-0.5 ${
                                    isLight ? 'text-gray-500' : 'text-white/40'
                                  }`}
                                >
                                  {child.description}
                                </span>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                  </div>
                );
              }
              return (
                <Button
                  key={item.url}
                  asChild
                  variant="ghost"
                  className={`w-full justify-start text-lg ${
                    location.pathname === item.url
                      ? isLight
                        ? 'text-[#7950A2] font-bold bg-[#F2ECF7]'
                        : 'text-white font-semibold'
                      : isLight
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white/80 hover:text-white'
                  }`}
                >
                  <Link to={item.url} onClick={() => setOpen(false)}>
                    {item.label}
                  </Link>
                </Button>
              );
            })}

          <div className={`h-px w-full my-2 ${isLight ? 'bg-gray-200' : 'bg-white/10'}`}></div>

          <Button
            variant="ghost"
            className={`w-full justify-start ${
              isLight ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => window.location.assign(getAuthLink('/login'))}
          >
            <User className="mr-2 size-4" />
            Login
          </Button>
          <Button
            className="w-full bg-[#7950A2] text-white hover:bg-[#68418f] font-medium"
            onClick={() => window.location.assign(getAuthLink('/register'))}
          >
            Get Early Access
          </Button>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const MarketingLayout = () => {
  const location = useLocation();
  const layoutReducedMotion = useReducedMotion();
  // The history entry this layout mounted with (first visit or hard reload)
  // renders at full opacity at once. The first route change clears the key,
  // so every later location, including Back to that first entry, plays the
  // entrance animation below. Adjusting state during render (not in an
  // effect) means the new page mounts with the entrance already enabled.
  const [firstLocationKey, setFirstLocationKey] = useState<string | null>(location.key);
  if (firstLocationKey !== null && location.key !== firstLocationKey) {
    setFirstLocationKey(null);
  }
  const skipEntrance = layoutReducedMotion || location.key === firstLocationKey;
  // Initialize with hard-coded defaults immediately so nav renders without waiting for fetch.
  // The async load below will update if CMS returns valid items.
  const [menuItems, setMenuItems] = useState<MenuItem[]>(
    () => getDefaultNavigationSettings().menu_items
  );
  const [generalSettings, setGeneralSettings] = useState<{
    phone: string;
    email: string;
    address: string;
  } | null>(null);

  useEffect(() => {
    // Load navigation settings (only update if we get valid non-empty items back)
    loadNavigationSettings().then((nav) => {
      if (nav?.menu_items && nav.menu_items.length > 0 && isValidMenuConfig(nav.menu_items)) {
        setMenuItems(nav.menu_items);
      }
    });

    // Load general settings for footer
    loadGeneralSettings().then((settings) => {
      if (settings) {
        getFullAddress().then((address) => {
          setGeneralSettings({
            phone: settings.phone,
            email: settings.email,
            address,
          });
        });
      }
    });
  }, []);

  const fallbackSeo =
    fallbackSeoByPath.find((item) =>
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
    ) || fallbackSeoByPath[fallbackSeoByPath.length - 1];

  const fallbackSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fallbackSeo.title,
    description: fallbackSeo.description,
    url: `https://www.rooflyzer.pro${location.pathname}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Rooflyzer OS',
      url: 'https://www.rooflyzer.pro',
    },
  };

  const isHomePage = location.pathname === '/';

  const renderMenuItem = (item: MenuItem, isMobile = false) => {
    if (item.children && item.children.length > 0) {
      if (isMobile) {
        return (
          <div key={item.url} className="space-y-1">
            <div
              className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 ${
                isHomePage ? 'text-gray-500' : 'text-white/80'
              }`}
            >
              {item.label}
            </div>
            {item.children
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((child) => (
                <Button
                  key={child.url}
                  asChild
                  variant="ghost"
                  size="sm"
                  className={`w-full justify-start pl-6 ${
                    location.pathname === child.url
                      ? isHomePage
                        ? 'text-[#7950A2] bg-[#F2ECF7] font-semibold'
                        : 'text-white font-semibold'
                      : isHomePage
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white/70 hover:text-white'
                  }`}
                >
                  <Link to={child.url}>{child.label}</Link>
                </Button>
              ))}
          </div>
        );
      }
      return (
        <DropdownMenu key={item.url}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={
                location.pathname.startsWith(item.url) && item.url !== '#'
                  ? isHomePage
                    ? 'text-[#7950A2] bg-[#F2ECF7] font-medium'
                    : 'text-white bg-white/10 font-medium'
                  : isHomePage
                    ? 'text-gray-700 hover:text-[#181B20] hover:bg-gray-100 transition-colors'
                    : 'text-white/70 hover:text-white hover:bg-white/5 transition-colors'
              }
            >
              {item.label}
              <ChevronDown className="ml-1 size-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className={`w-[480px] p-3 rounded-2xl shadow-xl focus:outline-none mt-2 ${
              isHomePage
                ? 'bg-white/98 backdrop-blur-2xl border-gray-200 text-[#181B20]'
                : 'bg-[#0a0a0c]/95 backdrop-blur-2xl border-white/10 text-white'
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.25, ease: FLOW_EASE }}
              className="grid grid-cols-2 gap-1 focus:outline-none"
            >
              {item.children
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((child) => {
                  const IconComponent = child.icon
                    ? (
                        Icons as unknown as Record<
                          string,
                          React.ComponentType<{ className?: string }>
                        >
                      )[child.icon]
                    : null;
                  return (
                    <DropdownMenuItem
                      asChild
                      key={child.url}
                      className={`p-3 cursor-pointer rounded-xl flex items-start gap-3 transition-colors outline-none h-auto border border-transparent ${
                        isHomePage
                          ? 'hover:bg-gray-50 focus:bg-gray-100 hover:border-gray-200'
                          : 'hover:bg-white/[0.04] focus:bg-white/[0.06] hover:border-white/[0.06]'
                      }`}
                    >
                      <Link to={child.url} className="flex flex-col h-full w-full">
                        <div
                          className={`flex items-center gap-2 font-medium ${
                            isHomePage ? 'text-[#181B20]' : 'text-white/95'
                          }`}
                        >
                          {IconComponent && (
                            <IconComponent
                              className={`size-4 ${isHomePage ? 'text-[#7950A2]' : 'text-emerald-400'}`}
                            />
                          )}
                          {child.label}
                        </div>
                        {child.description && (
                          <span
                            className={`text-xs mt-1 line-clamp-2 ${
                              isHomePage ? 'text-gray-500' : 'text-white/50'
                            }`}
                          >
                            {child.description}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
            </motion.div>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    return (
      <Button
        key={item.url}
        asChild
        variant="ghost"
        size="sm"
        className={
          location.pathname === item.url
            ? isHomePage
              ? 'text-[#7950A2] bg-[#F2ECF7] font-semibold'
              : 'text-white bg-white/10 font-medium'
            : isHomePage
              ? 'text-gray-700 hover:text-[#181B20] hover:bg-gray-100 transition-colors'
              : 'text-white/70 hover:text-white hover:bg-white/5 transition-colors'
        }
      >
        <Link to={item.url}>{item.label}</Link>
      </Button>
    );
  };

  return (
    <div
      data-theme={isHomePage ? 'marketing-light' : 'marketing-dark'}
      className={`min-h-screen w-full max-w-full flex flex-col relative ${
        isHomePage ? 'bg-[#FFFFFF] text-[#181B20]' : 'bg-[#050507] text-white'
      }`}
    >
      <SEOHead
        title={fallbackSeo.title}
        description={fallbackSeo.description}
        canonicalUrl={location.pathname}
        schema={fallbackSchema}
      />

      {/* Ambient background for dark theme only */}
      {!isHomePage && (
        <>
          <div className="pointer-events-none fixed inset-0 -z-20 overflow-hidden">
            <div className="absolute bottom-0 right-0 w-[600px] h-[500px] rounded-full blur-[140px] bg-[radial-gradient(circle_at_center,rgba(30,41,59,0.25)_0%,rgba(5,5,7,0)_70%)]" />
          </div>
          <div className="pointer-events-none fixed inset-0 -z-10 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]" />
          <div className="pointer-events-none fixed top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-50" />
        </>
      )}

      {/* Header — floating glass pill */}
      <header
        className={
          isHomePage
            ? 'sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md'
            : 'sticky top-0 z-50 w-full px-4 sm:px-6 py-3 sm:py-4'
        }
      >
        <div className={isHomePage ? 'max-w-[1360px] mx-auto px-5 sm:px-8' : 'max-w-7xl mx-auto'}>
          <div
            className={`flex justify-between items-center transition-colors ${
              isHomePage
                ? 'h-16 sm:h-[72px]'
                : 'px-4 h-14 rounded-xl bg-[#050507]/80 backdrop-blur-xl border border-white/[0.06]'
            }`}
          >
            {/* Left Side: Rooflyzer Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src="/brand/rooflyzer-logo.png"
                alt="Rooflyzer"
                className="h-6 sm:h-7 w-auto max-w-[128px] object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav aria-label="Main navigation" className="hidden xl:flex items-center gap-1">
              {menuItems
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((item) => renderMenuItem(item, false))}
              <a
                href={getAppLink('/qr-hub-landing')}
                className={buttonVariants({
                  variant: 'ghost',
                  size: 'sm',
                  className: isHomePage
                    ? 'text-gray-700 hover:text-[#181B20] hover:bg-gray-100 transition-colors'
                    : 'text-white/70 hover:text-white hover:bg-white/5 transition-colors',
                })}
              >
                QR Print Shop
              </a>
            </nav>

            {/* Right Actions */}
            <div className="hidden xl:flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.assign(getAuthLink('/login'))}
                className={
                  isHomePage
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }
              >
                <LogIn className="mr-1.5 size-4" />
                Sign In
              </Button>
              {isHomePage && (
                <Link
                  to="/demo"
                  className="inline-flex h-10 items-center rounded-full border border-[#7950A2] px-5 text-sm font-medium text-[#7950A2] transition-colors hover:bg-[#F4EFF8]"
                >
                  Book a demo
                </Link>
              )}
              <Button
                size="sm"
                onClick={() => window.location.assign(getAuthLink('/register'))}
                className={
                  isHomePage
                    ? 'h-10 rounded-full bg-[#7950A2] px-5 text-sm font-medium text-white hover:bg-[#68418f]'
                    : 'bg-[#7950A2] text-white hover:bg-[#68418f] font-medium px-4 shadow-xs'
                }
              >
                {isHomePage ? 'Get started' : 'Get Early Access'}
              </Button>
            </div>

            {/* Mobile Navigation Toggle */}
            <div className="xl:hidden flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className={
                  isHomePage
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 h-9 px-3 text-sm'
                    : 'text-white/80 hover:text-white hover:bg-white/10 h-9 px-3 text-sm'
                }
                onClick={() => window.location.assign(getAuthLink('/login'))}
              >
                <LogIn className="size-4 mr-1.5" />
                Sign In
              </Button>
              <MobileMenu menuItems={menuItems} isLight={isHomePage} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content — route transition, ENTER-ONLY: a keyed motion.main
          replays its entrance per pathname. No AnimatePresence/exit: Outlet
          reads current router context, so an exiting wrapper would render
          the NEW page (and the destination would double-mount). Reduced
          motion and the first location: no entrance animation. */}
      <motion.main
        key={location.pathname}
        className="flex-grow"
        initial={skipEntrance ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: FLOW_EASE }}
      >
        <Outlet />
      </motion.main>

      {/* Global Script Injection */}
      <ScriptInjector useGlobalScripts={true} />

      {/* Footer */}
      <footer
        className={`py-16 sm:py-20 mt-auto ${
          isHomePage
            ? 'bg-[#F9FAFB] border-t border-gray-200 text-gray-600'
            : 'border-t border-white/[0.06] text-white/70'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8 mb-16">
            <div className="col-span-2 md:col-span-4">
              <Link to="/" className="inline-flex items-center gap-2.5 mb-5">
                <img
                  src="/brand/rooflyzer-logo.png"
                  alt="Rooflyzer"
                  className="h-7 w-auto object-contain"
                />
              </Link>
              <p
                className={`text-sm leading-relaxed max-w-xs ${
                  isHomePage ? 'text-gray-500' : 'text-white/55'
                }`}
              >
                The complete operating system for modern roofing businesses. From lead to invoice,
                all in one place.
              </p>
            </div>

            {Object.entries(footerLinkGroups).map(([title, links]) => (
              <div key={title} className="md:col-span-2">
                <h3
                  className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
                    isHomePage ? 'text-gray-500' : 'text-white/40'
                  }`}
                >
                  {title}
                </h3>
                <ul className="space-y-2.5 text-sm">
                  {links.map(({ label, to }) => (
                    <li key={to}>
                      <Link
                        to={to}
                        className={`transition-colors ${
                          isHomePage
                            ? 'text-gray-600 hover:text-[#181B20]'
                            : 'text-white/70 hover:text-white'
                        }`}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div
            className={`flex flex-col md:flex-row justify-between items-center pt-8 border-t gap-4 ${
              isHomePage ? 'border-gray-200 text-gray-500' : 'border-white/[0.06] text-white/40'
            }`}
          >
            <p className="text-sm">
              © {new Date().getFullYear()} Rooflyzer Inc. All rights reserved.
            </p>
            {generalSettings && (
              <div
                className={`text-xs text-center md:text-right ${
                  isHomePage ? 'text-gray-500' : 'text-white/30'
                }`}
              >
                <p>{generalSettings.address}</p>
                <p className="mt-1">
                  <a
                    href={`tel:${generalSettings.phone?.replace(/[^\d]/g, '') ?? ''}`}
                    className="hover:underline transition-colors"
                  >
                    {generalSettings.phone}
                  </a>
                  {' • '}
                  <a
                    href={`mailto:${generalSettings.email}`}
                    className="hover:underline transition-colors"
                  >
                    {generalSettings.email}
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};
